import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  Skeleton,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ConfirmationModal from "components/modals/confirmation-modal";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { PermissionsWrapper } from "components/permissions/permissions";
import { usePermissions } from "components/permissions/usePermissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { OrgMembership, Role } from "data/models";
import { updateUserRoleOrgMembership } from "data/mutations-common";
import {
  fetchMemberUserDetails,
  fetchOrganisation,
  fetchUserOrgMembership,
} from "data/queries-common";
import { useState } from "react";
import { generatePath, useParams } from "react-router";
import { Link as RouterLink } from "react-router-dom";
import * as Routes from "routes";
import { formatTimestamp } from "utils";
import { ResetTwoFactorCard, RoleSelector, UserCard } from "./cards";

const OrgMemberDetails = () => {
  let { accountId, orgId } = useParams<{ accountId: string; orgId: string }>();

  const [isTransferOwnershipModalOpen, setIsTransferOwnershipModalOpen] =
    useState(false);
  const [confirmationName, setConfirmationName] = useState("");

  const [identity] = useIdentity();
  const { enqueueSupportSnackbar, enqueueInfoSnackbar } = useCustomSnackbars();

  const { isPermitted: thisOrgIsPermitted } = usePermissions();

  const userLink = generatePath(Routes.orgMembers);
  const queryClient = useQueryClient();

  const memberAccountDetailsQuery = useQuery({
    queryKey: [QueryKeys.organisationMemberAccount, accountId],
    queryFn: () => fetchMemberUserDetails(accountId),
    enabled: !!accountId,
  });

  const orgMembershipQuery = useQuery({
    queryKey: [QueryKeys.organisationMemberDetails, accountId],
    queryFn: () => fetchUserOrgMembership(accountId),
    enabled: !!accountId,
  });

  const organisationQuery = useQuery({
    queryKey: [QueryKeys.organisation],
    queryFn: () => fetchOrganisation(),
  });

  const updateOrgMembershipMutation = useMutation({
    mutationFn: ({ role }: { role: string }) =>
      updateUserRoleOrgMembership(role, accountId, orgId),
    onSuccess: (membership: OrgMembership) => {
      queryClient.setQueryData(
        [QueryKeys.organisationMemberDetails, accountId], // Add Org ID member can belong to different orgs
        membership,
      );
    },
    onError: (error: Error) => {
      enqueueSupportSnackbar(`${error.toString()}`, "error");
    },
  });

  const transferOwnershipMutation = useMutation({
    mutationFn: () => updateUserRoleOrgMembership(Role.OWNER, accountId, orgId),
    onSuccess: () => {
      // Get organization and member details for the success message
      const orgName = organisationQuery.data?.name;
      const memberName =
        memberAccountDetailsQuery.data?.firstName &&
        memberAccountDetailsQuery.data?.lastName
          ? `${memberAccountDetailsQuery.data.firstName} ${memberAccountDetailsQuery.data.lastName}`
          : memberAccountDetailsQuery.data?.email;

      // Show success snackbar
      enqueueInfoSnackbar(
        `Ownership of ${orgName} has been successfully transferred to ${memberName}. Your role has been updated to Admin.`,
        "success",
      );

      // Close the modal and reset state
      setIsTransferOwnershipModalOpen(false);
      setConfirmationName("");

      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.organisationMemberDetails, accountId],
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.organisationInvitations],
      });
      queryClient.invalidateQueries({ queryKey: [QueryKeys.permissions] });
    },
    onError: (error: Error) => {
      // Show error snackbar and keep modal open so user can try again
      enqueueSupportSnackbar(error.message, "error");
    },
  });

  return (
    <>
      <Box position="absolute" top={32} right={32}>
        <Button
          component={RouterLink}
          to={userLink}
          variant="contained"
          color="primary"
        >
          Back
        </Button>
      </Box>

      <ReactQueryErrorWrapper
        queries={[
          memberAccountDetailsQuery,
          orgMembershipQuery,
          organisationQuery,
        ]}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CardHeader title="Basic Details" />
        </Grid>
        <Grid item xs={12} md={6}>
          {/* Current Plan Card */}
          {memberAccountDetailsQuery.isEnabled &&
            memberAccountDetailsQuery.isPending && (
              <Skeleton variant="rectangular" height={250}></Skeleton>
            )}
          {memberAccountDetailsQuery.isSuccess && (
            <UserCard
              name={
                memberAccountDetailsQuery.data.firstName +
                " " +
                memberAccountDetailsQuery.data.lastName
              }
              email={memberAccountDetailsQuery.data.email}
              status={memberAccountDetailsQuery.data.status}
              added={formatTimestamp(memberAccountDetailsQuery.data.created)}
            />
          )}
        </Grid>

        <Grid item xs={12} md={6}>
          <CardHeader
            title="Authentication"
            subheader="Control team access to features offered in Geoscape Data"
          />
        </Grid>
        <Grid item xs={12} md={6} justifyItems="right">
          <ResetTwoFactorCard />
        </Grid>

        {/* Transfer Ownership Row - Only show if member is not already the owner */}
        {orgMembershipQuery.data?.role !== Role.OWNER && (
          <PermissionsWrapper
            keys={[PermissionsKeys.membersChangeOwner]}
            messages={{
              [PermissionsReasons.ROLE_DENY]: <></>,
            }}
            showLoadingState={false}
          >
            <Grid item xs={12} md={6}>
              <CardHeader title="Transfer Ownership" />
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography sx={{ marginLeft: "14px", marginRight: "14px" }}>
                    Permanently transfer ownership of this organisation to{" "}
                    {memberAccountDetailsQuery.data?.firstName &&
                    memberAccountDetailsQuery.data?.lastName
                      ? `${memberAccountDetailsQuery.data.firstName} ${memberAccountDetailsQuery.data.lastName}`
                      : memberAccountDetailsQuery.data?.email || "this member"}
                    . This action cannot be undone.
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: "flex-end" }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setIsTransferOwnershipModalOpen(true)}
                    disabled={!accountId || !orgId}
                  >
                    Transfer Ownership
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </PermissionsWrapper>
        )}

        <Grid item xs={12} md={6}>
          <CardHeader
            title="Role"
            subheader="Manage the permission of this member in the organisation by their role"
          />
        </Grid>
        <Grid item xs={12} md={6} justifyItems="right">
          {orgMembershipQuery.isEnabled && orgMembershipQuery.isPending && (
            <Skeleton variant="rectangular" height={525}></Skeleton>
          )}
          {orgMembershipQuery.data?.role && (
            <RoleSelector
              memberRole={orgMembershipQuery.data?.role}
              onSubmit={updateOrgMembershipMutation}
            />
          )}
        </Grid>
      </Grid>

      <ConfirmationModal
        open={isTransferOwnershipModalOpen}
        handleClose={() => {
          setIsTransferOwnershipModalOpen(false);
          setConfirmationName("");
        }}
        header="Transfer Ownership"
        body=""
        leftButtonText="Cancel"
        rightButtonText="Transfer Ownership"
        leftButtonColor="primary"
        rightButtonColor="error"
        leftButtonVariant="outlined"
        rightButtonVariant="contained"
        handleLeftButton={() => {
          setIsTransferOwnershipModalOpen(false);
          setConfirmationName("");
        }}
        handleRightButton={() => {
          transferOwnershipMutation.mutate();
        }}
        rightButtonDisabled={
          confirmationName.trim() !== organisationQuery.data?.name?.trim() ||
          transferOwnershipMutation.isPending
        }
        isLoading={transferOwnershipMutation.isPending}
      >
        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography>
            This is a permanent and irreversible action.{" "}
            {memberAccountDetailsQuery.data?.firstName &&
            memberAccountDetailsQuery.data?.lastName
              ? `${memberAccountDetailsQuery.data.firstName} ${memberAccountDetailsQuery.data.lastName}`
              : memberAccountDetailsQuery.data?.email || "This member"}{" "}
            will have <b>full, unrestricted control</b> over the{" "}
            {organisationQuery.data?.name} organisation.
          </Typography>
          <Typography>
            Upon successful transfer your role will be immediately changed to{" "}
            <b>admin</b>.
          </Typography>

          <Typography>
            To confirm, please type your organisation's name
            <br />
            <b>{organisationQuery.data?.name}</b>
          </Typography>

          <TextField
            size="small"
            label={`Type "${organisationQuery.data?.name}" to confirm`}
            value={confirmationName}
            onChange={(e) => setConfirmationName(e.target.value)}
            placeholder="Organisation name"
            fullWidth
          />
        </Box>
      </ConfirmationModal>
    </>
  );
};

export default OrgMemberDetails;
